import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopyOutlined as ContentCopyIcon,
  EmailOutlined as EmailIcon,
  SmsOutlined as SmsIcon,
  VisibilityOutlined as VisibilityIcon,
  VisibilityOffOutlined as VisibilityOffIcon,
} from '@mui/icons-material';
import api from '../api';
import {
  formatSin,
  sinRequestMailto,
  sinRequestMessage,
  sinRequestSms,
  sinStatus,
} from '../utils/applicantSin';

const TONE_COLOR = { warning: 'warning.main', info: 'text.secondary', success: 'success.main' };

/**
 * SIN row in the application details: view on demand (logged by the server) or send the
 * applicant a one-time secure link to enter it.
 */
const ApplicantSinField = ({ application, role, label = 'SIN/SSN' }) => {
  const [revealed, setRevealed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState('');
  const [requestLink, setRequestLink] = useState(null);
  const [sentLocally, setSentLocally] = useState(null);

  const applicationId = application?.id;
  useEffect(() => {
    setRevealed(null);
    setError(null);
    setRequestLink(null);
    setSentLocally(null);
  }, [applicationId, role]);

  const person = application?.[role];
  if (!person) return null;

  const target = {
    propertyId: application.propertyId,
    unitId: application.unitId,
    applicationId: application.id,
    role,
  };
  const quarantined = Boolean(application.sinQuarantine?.[role]);
  const status = sinStatus(
    sentLocally ? { ...application, sinRequest: { ...application.sinRequest, [role]: sentLocally } } : application,
    role
  );
  const canShow = Boolean(person.sin) && !quarantined;

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 2000);
    } catch {
      setError('Could not copy. Select the text and copy it manually.');
    }
  };

  const handleShow = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/api/applicant-sin/reveal', target);
      setRevealed(res.data?.sin || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the SIN.');
    } finally {
      setBusy(false);
    }
  };

  const handleRequest = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/api/applicant-sin/requests', target);
      setRequestLink(res.data);
      setSentLocally({ requestedAt: new Date().toISOString(), expiresAt: res.data?.expiresAt });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create the link.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box data-testid={`sin-field-${role}`}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body1" data-clarity-mask="true" sx={{ fontFamily: revealed ? 'monospace' : undefined }}>
          {revealed ? formatSin(revealed) : person.sin ? 'XXX-XXX-XXXX' : 'Not provided'}
        </Typography>
        {revealed ? (
          <>
            <Tooltip title={copied === 'sin' ? 'Copied' : 'Copy SIN'}>
              <IconButton size="small" aria-label="Copy SIN" onClick={() => copy(revealed, 'sin')}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Hide SIN">
              <IconButton size="small" aria-label="Hide SIN" onClick={() => setRevealed(null)}>
                <VisibilityOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          canShow && (
            <Button size="small" startIcon={<VisibilityIcon />} onClick={handleShow} disabled={busy}>
              Show
            </Button>
          )
        )}
        <Button size="small" variant="outlined" onClick={handleRequest} disabled={busy}>
          Request SIN
        </Button>
      </Box>
      {status.text && (
        <Typography variant="caption" sx={{ color: TONE_COLOR[status.tone] }}>
          {status.text}
        </Typography>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

      <Dialog open={Boolean(requestLink)} onClose={() => setRequestLink(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Send the secure SIN link</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Send this link to {requestLink?.firstName || 'the applicant'} by email or text. It works once and
            expires in 7 days. Their SIN is saved encrypted on this application, and any earlier link stops working.
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={requestLink?.link || ''}
            InputProps={{ readOnly: true }}
            inputProps={{ 'aria-label': 'Secure SIN link' }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              href={requestLink ? sinRequestMailto(requestLink) : undefined}
              disabled={!requestLink?.email}
            >
              Email
            </Button>
            <Button
              variant="outlined"
              startIcon={<SmsIcon />}
              href={requestLink ? sinRequestSms(requestLink) : undefined}
              disabled={!requestLink?.phone}
            >
              Text
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => copy(sinRequestMessage(requestLink), 'message')}
            >
              {copied === 'message' ? 'Copied' : 'Copy message'}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Email and Text open your own email or messaging app with the message ready to send.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestLink(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApplicantSinField;
